(function () {
  const pad = (value) => String(value).padStart(2, "0");

  // share of the viewport the section must cover before the media fade runs —
  // measured against the viewport, not the section, so a section taller than
  // the screen still qualifies (it can never be "fully" visible)
  const APPEAR_VIEWPORT_RATIO = 0.5;

  // caps the gap a single frame may add to the countdown, so a long task or a
  // return from a background tab can't jump the progress bar forward
  const AUTOPLAY_FRAME_CAP_MS = 100;

  const playSlideVideos = (slide) => {
    if (!slide) return;

    slide.querySelectorAll("video.slideshow-slide__video").forEach((video) => {
      video.play().catch(() => {});
    });

    slide
      .querySelectorAll("iframe.slideshow-slide__video")
      .forEach((iframe) => {
        if (iframe.classList.contains("js-youtube")) {
          iframe.contentWindow?.postMessage(
            '{"event":"command","func":"playVideo","args":""}',
            "*"
          );
        } else if (iframe.classList.contains("js-vimeo")) {
          iframe.contentWindow?.postMessage('{"method":"play"}', "*");
        }
      });
  };

  const pauseSlideVideos = (slide) => {
    if (!slide) return;

    slide.querySelectorAll("video.slideshow-slide__video").forEach((video) => {
      video.pause();
    });

    slide
      .querySelectorAll("iframe.slideshow-slide__video")
      .forEach((iframe) => {
        if (iframe.classList.contains("js-youtube")) {
          iframe.contentWindow?.postMessage(
            '{"event":"command","func":"pauseVideo","args":""}',
            "*"
          );
        } else if (iframe.classList.contains("js-vimeo")) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
        }
      });
  };

  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    playSlideVideos(swiper.slides[swiper.activeIndex]);
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    pauseSlideVideos(swiper.slides[swiper.activeIndex]);
  };

  class Slideshow {
    constructor(section) {
      this.section = section;
      this.root = section.querySelector(".slideshow");
      this.sliderEl = section.querySelector(".js-slideshow-swiper");
      this.countEl = section.querySelector(".slider-count__current");
      this.swiper = null;
      this.paginationEl = section.querySelector(".slider-pagination");
      this.hoverEl = section.querySelector(".slideshow-wrapper");
      this.autoplayObserver = null;
      this.autoplayFrame = null;
      this.autoplayProgress = 0;
      this.autoplayFilled = false;
      this.autoplayOffscreen = false;
      this.dragging = false;
      this.onPointerCancel = null;
      this.appearHandler = null;
      this.appearFrame = null;
      this.countTimer = null;

      if (this.sliderEl) this.initSlider();
      this.initAppearance();
    }

    destroy() {
      if (this.onPointerCancel) {
        document.removeEventListener("pointercancel", this.onPointerCancel);
        this.onPointerCancel = null;
      }
      if (this.swiper) {
        this.swiper.slides?.forEach((slide) => slide.removeAttribute("inert"));
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      if (this.autoplayObserver) {
        this.autoplayObserver.disconnect();
        this.autoplayObserver = null;
      }
      this.stopAppearance();
      this.stopAutoplay();
      if (this.countTimer) {
        clearTimeout(this.countTimer);
        this.countTimer = null;
      }
    }

    initSlider() {
      const sliderEl = this.sliderEl;
      if (sliderEl.swiper) sliderEl.swiper.destroy();

      const animationType = sliderEl.dataset?.animationType || "slide";
      const nextBtn = this.section.querySelector(".slider-nav-button--next");
      const prevBtn = this.section.querySelector(".slider-nav-button--prev");
      const sliderLoop = sliderEl.getAttribute("data-loop") === "true";

      this.hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
      this.pauseAutoplayOnHover =
        sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
      this.autoplayDelay = Number(sliderEl.dataset.autoplayDelay) || 5000;
      const hasParallax = sliderEl.getAttribute("data-parallax") === "true";
      const sectionWidth =
        sliderEl.getAttribute("data-section-width") || "standard";
      const self = this;
      let outgoingSlide = null;

      this.swiper = new Swiper(sliderEl, {
        loop: sliderLoop,
        slidesPerView: 1,
        spaceBetween: 0,
        speed: animationType === "creative" ? 1400 : 800,
        watchSlideProgress: true,
        allowTouchMove: true,
        effect: animationType,
        // creative = vertical media switch with a slight angle and zoom
        creativeEffect:
          animationType === "creative"
            ? {
                prev: {
                  shadow: true,
                  translate: [0, "-30%", -1],
                  rotate: [0, 0, -2],
                  // sole source of the outgoing media's zoom (the media item's own
                  // scale never changes, see section-slideshow.scss) — linear so it
                  // grows at a constant rate over the whole exit
                  scale: 1.17,
                },
                next: {
                  translate: [0, "104%", 0],
                  rotate: [0, 0, 2],
                  scale: 1.04,
                },
              }
            : {},
        resistanceRatio: animationType === "creative" ? 0 : 0.85,
        // one slide fills the whole section, so Swiper's default long-swipe
        // rule (half the slide width before it commits) asks for a drag no
        // visitor makes — anything under it snapped back to the same slide
        longSwipesRatio: 0.1,
        parallax: hasParallax,
        mousewheel: {
          forceToAxis: true,
        },
        // the countdown runs on our own clock instead, see initAutoplay()
        autoplay: false,
        pagination: this.paginationEl
          ? { el: this.paginationEl, type: "progressbar" }
          : false,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn,
          disabledClass: "swiper-button-disabled",
        },
        breakpoints: {
          576: {
            spaceBetween: sectionWidth === "standard" ? 20 : 0,
          },
        },
        on: {
          init(swiper) {
            self.updateCount(swiper.realIndex);
            self.updateSlideFocusability(swiper);
          },
          slideChange(swiper) {
            self.updateCount(swiper.realIndex);
            self.updateSlideFocusability(swiper);
            self.resetAutoplayProgress();
          },
          slidesUpdated(swiper) {
            self.updateSlideFocusability(swiper);
          },
          touchStart() {
            self.dragging = true;
          },
          touchEnd() {
            self.dragging = false;
          },
          slideChangeTransitionStart() {
            playVideo(this);
            // the outgoing slide is still on screen, and pausing its video mid
            // transition freezes a visible frame and makes Safari re-rasterise
            // the layer, which flickers the overlay above it — hold it until
            // the slide is gone (captured now, loopFix can reorder the slides)
            outgoingSlide = this.slides[this.previousIndex] || null;
          },
          slideChangeTransitionEnd() {
            if (
              outgoingSlide &&
              outgoingSlide !== this.slides[this.activeIndex]
            ) {
              pauseSlideVideos(outgoingSlide);
            }
            outgoingSlide = null;
          },
        },
      });

      // the browser can take the pointer away mid-drag (it starts a native
      // image drag, a video iframe grabs it, the gesture leaves the window):
      // Swiper ignores pointercancel outside Safari and never gets a matching
      // pointerup, so it keeps the gesture "in progress" and drops every swipe
      // that follows — clear its touch state ourselves
      this.onPointerCancel = () => {
        const data = this.swiper?.touchEventsData;
        if (!data) return;
        this.dragging = false;
        data.pointerId = null;
        data.touchId = null;
        data.isTouched = false;
        data.isMoved = false;
        data.startMoving = false;
      };
      document.addEventListener("pointercancel", this.onPointerCancel);

      this.initAutoplayObserver();
      this.initAutoplay();
    }

    // Swiper's own autoplay is off on purpose: it pauses and resumes its timer
    // from three uncoordinated places (its hover handlers, every slide
    // transition, the viewport observer below) and a second pause before a
    // resume rewrites the time it has left, so the bar jumped, restarted
    // mid-slide or sat dead after a hover. On our own clock the bar and the
    // switch read the same value: it fills to the end, then the slide moves.
    initAutoplay() {
      if (!this.hasAutoplay || !this.swiper) return;

      let lastFrame = null;

      const runAutoplay = (time) => {
        this.autoplayFrame = requestAnimationFrame(runAutoplay);

        const swiper = this.swiper;
        if (!swiper || swiper.destroyed) return;

        const elapsed =
          lastFrame === null
            ? 0
            : Math.min(time - lastFrame, AUTOPLAY_FRAME_CAP_MS);
        lastFrame = time;

        const held = this.isAutoplayHeld();
        if (!held) {
          this.autoplayProgress = Math.min(
            this.autoplayProgress + elapsed / this.autoplayDelay,
            1
          );
        }

        this.renderAutoplayProgress();

        if (held || swiper.animating || this.autoplayProgress < 1) return;
        // the filled bar gets one frame on screen before the slide moves
        if (!this.autoplayFilled) {
          this.autoplayFilled = true;
          return;
        }
        this.autoplayFilled = false;
        this.advanceAutoplay();
      };

      this.autoplayFrame = requestAnimationFrame(runAutoplay);
    }

    stopAutoplay() {
      if (!this.autoplayFrame) return;
      cancelAnimationFrame(this.autoplayFrame);
      this.autoplayFrame = null;
    }

    // :hover is polled per frame rather than tracked from pointerenter /
    // pointerleave: a leave event that never arrives (the cursor leaving the
    // window, a slide turning inert under it) leaves the countdown paused for
    // good, which is what killed the progress bar after hovering the banner
    isAutoplayHeld() {
      if (this.autoplayOffscreen || this.dragging) return true;
      // a slide change would make the keyboard-focused slide inert and drop focus
      // to <body>; a mouse-focused arrow must not hold it
      const focused = document.activeElement;
      if (
        focused &&
        this.section.contains(focused) &&
        focused.matches(":focus-visible")
      ) {
        return true;
      }
      if (!this.pauseAutoplayOnHover || !this.hoverEl) return false;
      return this.hoverEl.matches(":hover");
    }

    // the fill belongs to Swiper's pagination, which also puts a slide-index
    // transform and a speed-long transition on it at every slide change —
    // overwrite both, or the bar lags seconds behind the value it is given
    renderAutoplayProgress() {
      const fill = this.paginationEl?.querySelector(
        ".swiper-pagination-progressbar-fill"
      );
      if (!fill) return;
      fill.style.transitionDuration = "0ms";
      fill.style.transform = `scaleX(${this.autoplayProgress})`;
    }

    // any slide change restarts the countdown, autoplay's own or a visitor's
    resetAutoplayProgress() {
      if (!this.autoplayFrame) return;
      this.autoplayProgress = 0;
      this.autoplayFilled = false;
      this.renderAutoplayProgress();
    }

    advanceAutoplay() {
      const swiper = this.swiper;
      if (swiper.isEnd && !swiper.params.loop && !swiper.params.rewind) {
        swiper.slideTo(0);
        return;
      }
      swiper.slideNext();
    }

    // off-screen slides keep their links tabbable, and focusing one scrolls the
    // hidden slide into view over the active one — `inert` keeps Tab inside the
    // visible slide (pointer-events alone only covers the mouse)
    updateSlideFocusability(swiper) {
      if (!swiper?.slides) return;

      swiper.slides.forEach((slide, index) => {
        slide.toggleAttribute("inert", index !== swiper.activeIndex);
      });
    }

    // old value rolls up, the new one rises from below inside the container
    updateCount(index) {
      const container = this.countEl;
      if (!container) return;

      const value = pad(index + 1);
      const active = container.querySelector(
        ".slider-count__value:not(.slider-count__value--exit)"
      );
      if (!active || active.textContent === value) return;

      container
        .querySelectorAll(".slider-count__value--exit")
        .forEach((el) => el.remove());

      const next = document.createElement("span");
      next.className = "slider-count__value slider-count__value--enter";
      next.textContent = value;
      container.appendChild(next);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          active.classList.add("slider-count__value--exit");
          next.classList.remove("slider-count__value--enter");
        });
      });

      if (this.countTimer) clearTimeout(this.countTimer);
      this.countTimer = setTimeout(() => {
        container
          .querySelectorAll(".slider-count__value--exit")
          .forEach((el) => el.remove());
      }, 600);
    }

    initAutoplayObserver() {
      const sliderEl = this.sliderEl;
      if (!sliderEl || !sliderEl.swiper) return;

      this.autoplayObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!sliderEl.swiper) return;
          this.autoplayOffscreen = !entry.isIntersecting;
          if (entry.isIntersecting) {
            playVideo(sliderEl.swiper);
          } else {
            stopVideo(sliderEl.swiper);
          }
        });
      });

      this.autoplayObserver.observe(this.section);
    }

    // untransformed edges of the first slide's content group — the blocks are
    // translated (and clipped) out of view before the reveal, so their own
    // rects would report where they animate FROM, not where they land
    contentBounds() {
      const content = this.section.querySelector(".slideshow-slide__content");
      const first = content?.firstElementChild;
      const last = content?.lastElementChild;
      if (!content || !first || !last) return null;

      const contentTop = content.getBoundingClientRect().top;

      return {
        top: contentTop + first.offsetTop,
        bottom: contentTop + last.offsetTop + last.offsetHeight,
      };
    }

    // media fade starts once the section covers enough of the screen, the
    // creative reveal once the copy itself is in view — a section taller than
    // the viewport (4:5, full height, lots of blocks) would otherwise only
    // qualify after it had been scrolled past, or never on the last section.
    // A scroll check rather than an IntersectionObserver: while a tall section
    // fully covers the viewport its ratio stops changing, so no threshold is
    // ever crossed and the copy can reach the screen unnoticed. The listener
    // removes itself as soon as both animations have run.
    initAppearance() {
      const root = this.root;
      if (!root) return;

      const wantsAppear = root.dataset.appearanceAnimation === "true";
      const wantsReveal = this.sliderEl?.dataset.animationType === "creative";

      if (!wantsAppear && !wantsReveal) return;

      let appeared = !wantsAppear;
      let revealed = !wantsReveal;

      const check = () => {
        const rect = this.section.getBoundingClientRect();
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight;

        if (rect.bottom > 0 && rect.top < viewportHeight) {
          if (!appeared) {
            const visible =
              Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);

            if (
              visible >=
              Math.min(rect.height, viewportHeight) * APPEAR_VIEWPORT_RATIO
            ) {
              root.classList.add("slideshow--appeared");
              appeared = true;
            }
          }

          if (!revealed) {
            // a copy group taller than the viewport falls back to its own top
            const copy = this.contentBounds();

            if (!copy || copy.bottom <= viewportHeight || copy.top <= 0) {
              root.classList.add("slideshow--revealed");
              revealed = true;
            }
          }
        }

        if (appeared && revealed) this.stopAppearance();
      };

      this.appearHandler = () => {
        if (this.appearFrame) return;
        this.appearFrame = requestAnimationFrame(() => {
          this.appearFrame = null;
          check();
        });
      };

      window.addEventListener("scroll", this.appearHandler, { passive: true });
      window.addEventListener("resize", this.appearHandler);
      // media loading above the section can slide it into view without a scroll
      window.addEventListener("load", this.appearHandler);
      check();
    }

    stopAppearance() {
      if (this.appearHandler) {
        window.removeEventListener("scroll", this.appearHandler);
        window.removeEventListener("resize", this.appearHandler);
        window.removeEventListener("load", this.appearHandler);
        this.appearHandler = null;
      }
      if (this.appearFrame) {
        cancelAnimationFrame(this.appearFrame);
        this.appearFrame = null;
      }
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains("slideshow-section")) return;
    if (!section.querySelector(".slideshow")) return;

    if (section._slideshowInstance) {
      section._slideshowInstance.destroy();
      section._slideshowInstance = null;
    }
    section._slideshowInstance = new Slideshow(section);
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeSlideshowEvents) {
    window.__themeSlideshowEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (section._slideshowInstance) {
        section._slideshowInstance.destroy();
        section._slideshowInstance = null;
      }
    });
  }
})();
